import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Activity,
  AlertTriangle,
  ArrowRight,
  Building2,
  CalendarDays,
  CalendarClock,
  CheckCircle2,
  CircleDashed,
  Puzzle,
  Users,
  Workflow,
} from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { EmptyState, Panel, StatCard, StatusBadge, formatDate, formatDateTime } from "@/components/admin/ui";
import DemoRequestDrawer from "@/components/admin/DemoRequestDrawer";
import UpcomingDemosCard from "@/components/admin/UpcomingDemosCard";
import { usePolling } from "@/hooks/usePolling";
import { usePageLastUpdated } from "@/context/PageMetaContext";

const ORG_COLORS = {
  Active: "#10B981",
  Onboarding: "#0052FF",
  Paused: "#F59E0B",
  Suspended: "#EF4444",
  Inactive: "#94A3B8",
};

const rangeLabel = (key) => ({
  "7d": "Last 7 days",
  "30d": "Last 30 days",
  month: "This month",
  custom: "Custom range",
}[key] || "Last 7 days");

export default function AdminOverview() {
  const { API } = useAuth();
  const navigate = useNavigate();
  const [range, setRange] = useState("7d");
  const [customStart, setCustomStart] = useState("");
  const [customEnd, setCustomEnd] = useState("");
  const [data, setData] = useState(null);
  const [selectedDemo, setSelectedDemo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);

  const openUpcomingDemo = useCallback(async (demo) => {
    try {
      const { data: detail } = await axios.get(`${API}/admin/demo-requests/${demo.demo_request_id}`, { withCredentials: true });
      setSelectedDemo(detail.demo_request);
    } catch {
      setError("The requested demo could not be found.");
    }
  }, [API]);

  const load = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    if (!silent) setError("");
    try {
      const params = { range };
      if (range === "custom") {
        if (!customStart || !customEnd) {
          if (!silent) setLoading(false);
          return;
        }
        params.start = customStart;
        params.end = customEnd;
      }
      const { data: overview } = await axios.get(`${API}/admin/overview`, { params, withCredentials: true });
      setData(overview);
      setLastUpdated(Date.now());
    } catch (err) {
      if (!silent) setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [API, range, customStart, customEnd]);

  useEffect(() => {
    load();
  }, [load]);

  usePolling(() => load(true), 10000);

  const donut = useMemo(() => {
    const counts = data?.organization_status_counts || {};
    const total = Object.values(counts).reduce((sum, value) => sum + value, 0);
    if (!total) return { background: "#E2E8F0", total };
    let cursor = 0;
    const parts = Object.entries(counts).map(([status, count]) => {
      const start = cursor;
      cursor += (count / total) * 360;
      return `${ORG_COLORS[status] || "#94A3B8"} ${start}deg ${cursor}deg`;
    });
    return { background: `conic-gradient(${parts.join(", ")})`, total };
  }, [data]);

  const metrics = data?.metrics;

  usePageLastUpdated(lastUpdated, "overview-last-updated");

  return (
    <div className="space-y-6" data-testid="admin-overview-page">
      <div className="flex flex-wrap items-center justify-end gap-3" data-testid="overview-date-controls">
        {range === "custom" && (
          <div className="flex items-center gap-2">
            <input
              type="date"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              data-testid="overview-custom-start"
              className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm dark:border-white/15 dark:bg-white/5 dark:text-white"
            />
            <input
              type="date"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              data-testid="overview-custom-end"
              className="rounded-xl border border-slate-300 bg-white px-3 py-2 text-sm dark:border-white/15 dark:bg-white/5 dark:text-white"
            />
          </div>
        )}
        <label className="flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold text-slate-800 dark:border-white/15 dark:bg-white/5 dark:text-slate-100">
          <CalendarDays size={16} className="text-slate-400 dark:text-slate-500" />
          <select
            value={range}
            onChange={(e) => setRange(e.target.value)}
            data-testid="overview-range-select"
            className="bg-transparent focus:outline-none"
          >
            <option value="7d">Last 7 days</option>
            <option value="30d">Last 30 days</option>
            <option value="month">This month</option>
            <option value="custom">Custom range</option>
          </select>
        </label>
      </div>

      {error && <p className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm font-semibold text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-400" data-testid="overview-error">{error}</p>}
      {loading && !data ? (
        <div className="rounded-2xl border border-slate-200 bg-white p-10 text-center text-sm font-semibold text-slate-500 dark:border-white/10 dark:bg-[#111C33] dark:text-slate-400" data-testid="overview-loading">Loading platform overview…</div>
      ) : data && (
        <>
          <div className="grid gap-5 md:grid-cols-2 xl:grid-cols-5" data-testid="overview-metric-cards">
            <StatCard icon={Building2} label="Organizations" value={metrics.organizations.total} helper={`+${metrics.organizations.recent} in ${rangeLabel(range).toLowerCase()}`} tone="purple" testId="metric-organizations" />
            <StatCard icon={CalendarClock} label="Demo Requests" value={metrics.demo_requests.total} helper={`${metrics.demo_requests.total} total • ${metrics.demo_requests.scheduled || 0} scheduled`} tone="orange" testId="metric-demo-requests" />
            <StatCard icon={Users} label="Users" value={metrics.users.total} helper={`+${metrics.users.recent} in ${rangeLabel(range).toLowerCase()}`} tone="green" testId="metric-users" />
            <StatCard icon={Puzzle} label="Active Solutions" value={metrics.active_solutions.total} helper="Configured platform solutions" tone="purple" testId="metric-active-solutions" />
            <StatCard icon={Workflow} label="Workflow Runs" value={metrics.workflow_runs.total} helper={metrics.workflow_runs.total ? `${metrics.workflow_runs.successful} successful · ${metrics.workflow_runs.failed} failed` : "No workflow data"} tone="cyan" testId="metric-workflow-runs" />
          </div>

          <div className="grid gap-6 xl:grid-cols-3">
            <Panel title="Attention Required" testId="attention-panel">
              {data.attention.length === 0 ? (
                <EmptyState icon={CheckCircle2} title="Nothing requires your attention." message="New demo, onboarding, and invitation issues will appear here when they need review." testId="attention-empty" />
              ) : (
                <div className="space-y-3">
                  {data.attention.map((item) => (
                    <button
                      key={`${item.type}-${item.title}`}
                      type="button"
                      onClick={() => navigate(item.href)}
                      data-testid={`attention-${item.type}`}
                      className="flex w-full items-center gap-3 rounded-xl border border-slate-200 p-4 text-left transition-colors duration-200 hover:border-blue-200 hover:bg-blue-50/40 dark:border-white/10 dark:hover:border-blue-500/30 dark:hover:bg-blue-500/10"
                    >
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-400">
                        <AlertTriangle size={17} />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block text-sm font-bold text-slate-950 dark:text-white">{item.title}</span>
                        <span className="mt-0.5 block text-xs text-slate-500 dark:text-slate-400">{item.description}</span>
                      </span>
                      <ArrowRight size={16} className="text-slate-400 dark:text-slate-500" />
                    </button>
                  ))}
                </div>
              )}
            </Panel>

            <Panel
              title="Organizations by Status"
              testId="organizations-status-panel"
              action={<button type="button" onClick={() => navigate("/admin/organizations")} data-testid="view-all-organizations-btn" className="text-sm font-bold text-[#0052FF] hover:text-blue-700">View all organizations</button>}
            >
              {donut.total === 0 ? (
                <EmptyState icon={Building2} title="No organizations yet." message="Organizations will appear here after the separate onboarding process." testId="organizations-status-empty" />
              ) : (
                <div className="flex items-center gap-6">
                  <div className="relative h-40 w-40 shrink-0 rounded-full" style={{ background: donut.background }} data-testid="organizations-status-donut">
                    <div className="absolute inset-5 flex flex-col items-center justify-center rounded-full bg-white dark:bg-[#111C33]">
                      <span className="text-3xl font-extrabold text-slate-950 dark:text-white">{donut.total}</span>
                      <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">Total</span>
                    </div>
                  </div>
                  <div className="space-y-2">
                    {Object.entries(data.organization_status_counts).map(([status, count]) => (
                      <div key={status} className="flex items-center gap-2 text-sm" data-testid={`org-status-${status.toLowerCase()}`}>
                        <span className="h-2.5 w-2.5 rounded-full" style={{ background: ORG_COLORS[status] }} />
                        <span className="font-semibold text-slate-700 dark:text-slate-300">{count} {status}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </Panel>

            <Panel title={`Workflow Runs (${data.range.label})`} testId="workflow-runs-panel">
              {data.workflow_series.length === 0 ? (
                <EmptyState icon={Workflow} title="No activity for this period." message="Workflow run data will appear here when workflow monitoring is connected." testId="workflow-empty" />
              ) : (
                <div data-testid="workflow-chart" />
              )}
            </Panel>
          </div>

          <div className="grid gap-6 xl:grid-cols-3">
            <Panel
              title="Recent Demo Requests"
              testId="recent-demo-requests-panel"
              className="xl:col-span-2"
              action={<button type="button" onClick={() => navigate("/admin/demo-requests")} data-testid="view-all-demo-requests-btn" className="text-sm font-bold text-[#0052FF] hover:text-blue-700">View all demo requests</button>}
            >
              {data.recent_demo_requests.length === 0 ? (
                <EmptyState icon={CalendarClock} title="No demo requests yet." message="Requests submitted through the public Request Demo form will appear here." testId="recent-demo-empty" />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[760px] text-left text-sm" data-testid="recent-demo-requests-table">
                    <thead>
                      <tr className="border-b border-slate-200 text-xs uppercase tracking-[0.12em] text-slate-400 dark:border-white/10 dark:text-slate-500">
                        <th className="py-3 pr-4 font-bold">Company</th>
                        <th className="py-3 pr-4 font-bold">Contact</th>
                        <th className="py-3 pr-4 font-bold">Email</th>
                        <th className="py-3 pr-4 font-bold">Industry</th>
                        <th className="py-3 pr-4 font-bold">Submitted</th>
                        <th className="py-3 pr-4 font-bold">Status</th>
                        <th className="py-3 font-bold">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.recent_demo_requests.map((request) => (
                        <tr key={request.demo_request_id} className="border-b border-slate-100 last:border-0 dark:border-white/5">
                          <td className="py-4 pr-4 font-bold text-slate-950 dark:text-white">{request.company}</td>
                          <td className="py-4 pr-4 text-slate-600 dark:text-slate-400">{request.name}</td>
                          <td className="py-4 pr-4 text-slate-600 dark:text-slate-400">{request.email}</td>
                          <td className="py-4 pr-4 text-slate-600 dark:text-slate-400">{request.industry}</td>
                          <td className="py-4 pr-4 text-slate-600 dark:text-slate-400">{formatDate(request.submitted_at)}</td>
                          <td className="py-4 pr-4"><StatusBadge status={request.status} testId={`overview-demo-status-${request.demo_request_id}`} /></td>
                          <td className="py-4"><button type="button" onClick={() => setSelectedDemo(request)} data-testid={`overview-demo-view-${request.demo_request_id}`} className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-slate-50 dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/10">View</button></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Panel>

            <div className="space-y-6">
              <UpcomingDemosCard demos={data.upcoming_demos || []} onSelect={openUpcomingDemo} />

              <Panel title="Recent Activity" testId="recent-activity-panel">
                {data.recent_activity.length === 0 ? (
                  <EmptyState icon={Activity} title="No activity for this period." message="Platform events will appear here as real activity occurs." testId="recent-activity-empty" />
                ) : (
                  <div className="space-y-4">
                    {data.recent_activity.map((item, index) => (
                      <button key={`${item.type}-${item.timestamp}-${index}`} type="button" onClick={() => navigate(item.href)} data-testid={`activity-${index}`} className="flex w-full items-start gap-3 text-left">
                        <span className="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-400">
                          <Activity size={15} />
                        </span>
                        <span className="min-w-0 flex-1">
                          <span className="block text-sm font-bold text-slate-950 dark:text-white">{item.title}</span>
                          <span className="block truncate text-xs text-slate-500 dark:text-slate-400">{item.description}</span>
                        </span>
                        <span className="text-xs text-slate-400 dark:text-slate-500">{formatDateTime(item.timestamp)}</span>
                      </button>
                    ))}
                  </div>
                )}
              </Panel>

              <Panel title="Platform Health" testId="platform-health-panel">
                <div className="space-y-3">
                  {data.platform_health.map((item) => (
                    <div key={item.service} className="flex items-center justify-between gap-4" data-testid={`health-${item.service.toLowerCase().replace(/\s+/g, "-")}`}>
                      <span className="text-sm font-semibold text-slate-700 dark:text-slate-300">{item.service}</span>
                      <StatusBadge status={item.status} testId={`health-status-${item.service.toLowerCase().replace(/\s+/g, "-")}`} />
                    </div>
                  ))}
                </div>
                <p className="mt-4 flex items-center gap-2 text-xs text-slate-400 dark:text-slate-500">
                  <CircleDashed size={13} />
                  Only measurable services report live status.
                </p>
              </Panel>
            </div>
          </div>
        </>
      )}

      <DemoRequestDrawer
        request={selectedDemo}
        open={!!selectedDemo}
        onClose={() => setSelectedDemo(null)}
        onUpdated={() => load()}
      />
    </div>
  );
}
